import {
  type AccountPublic,
  accountPublicSchema,
  type CustomerBillingSummary,
  type CustomerLedgerEntry,
  customerBillingSummarySchema,
  customerLedgerEntrySchema,
} from '@noconflict/contracts';
import type { Supabase } from '../../shared/api/supabase';

/**
 * The customer's side of payment on account: how the org pays, and its balance and history. Every amount here
 * comes from functions that answer nothing while staff have hidden the org's billing info, so the page cannot show
 * what it was never sent.
 */

/** The payment mode and whether amounts are shown, for anyone who may order, pay or see billing. */
export async function getAccountPublic(
  client: Supabase,
  organizationId: string,
): Promise<AccountPublic | null> {
  const { data, error } = await client.rpc('org_account_public', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  return row ? accountPublicSchema.parse(row) : null;
}

/** Null when there is nothing the caller may see (no `billing.view`, hidden amounts, or no account history). */
export async function getBillingSummary(
  client: Supabase,
  organizationId: string,
): Promise<CustomerBillingSummary | null> {
  const { data, error } = await client.rpc('customer_billing_summary', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  const row = Array.isArray(data) ? data[0] : data;
  return row ? customerBillingSummarySchema.parse(row) : null;
}

/** Newest first. */
export async function getCustomerLedger(
  client: Supabase,
  organizationId: string,
): Promise<CustomerLedgerEntry[]> {
  const { data, error } = await client.rpc('customer_ledger', {
    p_organization_id: organizationId,
  });
  if (error) throw new Error(error.message);
  return customerLedgerEntrySchema.array().parse(data ?? []);
}
